/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useState } from 'react'
import { LeftOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import type { EditPlayerFormValues } from '@sportninja/common/features/profile/player'
import Text from 'src/components/ts-components/Text'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSidebarState } from 'src/hooks/useSidebarState'
import { MENU_HEIGHT } from 'src/components/css'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import SalesforceIdChip from 'src/features/team/player/components/SalesforceIdChip'
import { useEditPlayerContext } from './EditPlayerContext'
import { PLAYER_SETUP_ITEMS } from './playerSetupItems'
import HeaderCancelIcon from './components/HeaderCancelIcon'
import HeaderSaveIcon from './components/HeaderSaveIcon'
import PlayerFormSkeleton from './components/PlayerFormSkeleton'
import PersonalSection from './components/sections/PersonalSection'
import AddressSection from './components/sections/AddressSection'
import EmergencyContactSection from './components/sections/EmergencyContactSection'
import SocialProfilesSection from './components/sections/SocialProfilesSection'
import MembershipSection from './components/sections/MembershipSection'
import ConfirmDiscardChangesModal from './components/ConfirmDiscardChangesModal'

type EditPlayerModalContentProps = {
  isVisible: boolean
  transitionDuration: number
}

const EditPlayerModalContent: React.FC<EditPlayerModalContentProps> = ({
  isVisible,
  transitionDuration,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284

  const { isLoading, onCancel, salesforceId } = useEditPlayerContext()
  const { submitForm, dirty } = useFormikContext<EditPlayerFormValues>()

  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false)

  const handleSave = async () => {
    await submitForm()
  }

  // Cancel/Back guard: warn before discarding edits, but close straight away
  // when the form is untouched.
  const handleRequestClose = () => {
    if (dirty) {
      setIsConfirmDiscardOpen(true)
    } else {
      onCancel()
    }
  }

  return (
    <div
      css={css`
        ${overlayStyle}
        top: ${isMobile ? 48 : MENU_HEIGHT}px;
        left: ${sidebarWidth}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
        transition: opacity ${transitionDuration}ms ease-out,
          transform ${transitionDuration}ms ease-out, left 0.2s ease-in-out;
      `}
    >
      <div css={contentWrapperStyle}>
        <div css={headerContainerStyle}>
          <div css={leftSideStyle}>
            <button
              type='button'
              css={backLinkStyle}
              onClick={handleRequestClose}
            >
              <LeftOutlined aria-hidden='true' />
              <Text
                variant='captionXSmall'
                weight='semiBold'
                color={colors.NEUTRAL_50}
                style='margin: 0; letter-spacing: 1.44px;'
              >
                BACK
              </Text>
            </button>
            <Text
              variant='displaySmall'
              weight='semiBold'
              color={colors.WHITE}
              style={`${
                isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'
              }`}
            >
              {capitalizeWords('Edit Player')}
            </Text>
          </div>
          <div css={rightSideStyle}>
            <HeaderActionButton
              text='Cancel'
              onClick={handleRequestClose}
              variant='secondary'
              textWeight='medium'
              disabled={isLoading}
              icon={<HeaderCancelIcon />}
            />
            <HeaderActionButton
              text='Save'
              onClick={handleSave}
              variant='primary'
              textWeight='bold'
              disabled={isLoading}
              isLoading={isLoading}
              icon={<HeaderSaveIcon />}
            />
          </div>
        </div>

        {isLoading ? (
          <PlayerFormSkeleton stacked={isMobile} />
        ) : (
          <ScrollSpyLayout
            items={PLAYER_SETUP_ITEMS}
            stacked={isMobile}
            gap={24}
          >
            <ScrollSpyLayout.Sidebar
              width={256}
              ariaLabel='Player form categories'
            />
            <ScrollSpyLayout.Content
              ariaLabel='Player profile sections'
              paddingRight={16}
            >
              {/* Email and name are locked here — this modal only ever edits an
                  existing player, and they identify the player. */}
              <PersonalSection
                // Display-only `*` markers — validation stays the API's job.
                requiredNameFields
                requireEmail
                lockEmail
                lockNames
                // Canlan-only; renders null when the profile has no sf_id.
                extraIdChips={<SalesforceIdChip salesforceId={salesforceId} />}
              />
              <AddressSection />
              <EmergencyContactSection />
              <SocialProfilesSection />
              <MembershipSection />
            </ScrollSpyLayout.Content>
          </ScrollSpyLayout>
        )}
      </div>

      <ConfirmDiscardChangesModal
        open={isConfirmDiscardOpen}
        onCancel={() => setIsConfirmDiscardOpen(false)}
        onConfirm={onCancel}
      />
    </div>
  )
}

export default EditPlayerModalContent

// ---- styled ----
const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 15;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 88px 0px;

  @media (max-width: 768px) {
    max-width: none;
    padding: 0;
  }
`

const headerContainerStyle = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
  margin-bottom: 24px;
`

const leftSideStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const backLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const rightSideStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`
